import React, { FC, PropsWithChildren } from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";

import { typography } from "../../../globalStyles/common/fonts";
import ThemeSwitcher from "../../themeSwitcher/ThemeSwitcher";
import CurrentTime from "./CurrentTime";

type BreadCrumbsProps = {
  content: string;
  link?: string;
};

// A single crumb. Without `link` it renders as the trailing, non-navigable one.
const BreadCrumbs = ({ content, link }: BreadCrumbsProps) => (
  <Wrap>
    {link ? (
      <>
        <Link to={link}>{content}</Link>
        &nbsp;/&nbsp;
      </>
    ) : (
      content
    )}
  </Wrap>
);

export default BreadCrumbs;

type BlockProps = {
  pageTitle?: string;
  withCurrentTime?: boolean;
} & PropsWithChildren;

export const BreadCrumbsBlock: FC<BlockProps> = ({
  children,
  pageTitle,
  withCurrentTime = true,
}) => (
  <BlockWrap>
    <PageTitleBlockWrap>
      <BreadCrumbsBlockWrap $hasPageTitle={!!pageTitle}>{children}</BreadCrumbsBlockWrap>
      {pageTitle && <PageTitle>{pageTitle}</PageTitle>}
    </PageTitleBlockWrap>

    {withCurrentTime && (
      <SwitcherWrap>
        <CurrentTime />
        <ThemeSwitcher />
      </SwitcherWrap>
    )}
  </BlockWrap>
);

const PageTitle = styled.div`
  display: flex;
  flex-direction: row;
  align-items: baseline;
  white-space: nowrap;
  text-transform: none;
  gap: 0.75rem;
  min-width: 0;
  ${({ theme }) => theme.typography.header1};
  color: ${({ theme }) => theme.colors.fg.primary};
`;

const Wrap = styled.h1`
  display: flex;
  flex-direction: row;
  white-space: nowrap;
  text-transform: capitalize;
  justify-content: flex-start;
  font-size: 10px;
  margin: 0;
  border: none;
  color: ${({ theme }) => theme.colors.fg.main_primary};
`;

const BlockWrap = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  gap: 1rem;
`;

const SwitcherWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const PageTitleBlockWrap = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

export const BreadCrumbsBlockWrap = styled.div<{ $hasPageTitle: boolean }>`
  display: flex;
  flex-direction: row;
  /* Root has no crumbs; keep the row's height so its title and the switcher sit
     where every other page puts them. */
  min-height: 16px;
  margin-bottom: ${({ $hasPageTitle }) => ($hasPageTitle ? "0px" : "1rem")};

  a:link,
  a:visited {
    color: ${({ theme }) => theme.colors.fg.accent};
  }
  ${typography.smallText3}
`;
